/* eslint-disable class-methods-use-this */
import ReduceStore from "flux/lib/FluxReduceStore";

import { WebappExtension } from "../../../foundation/extensions";
import BarrelActionTypes from "../barrel/BarrelActionTypes";
import { AllPayloads } from "../payloads";

import * as Payloads from "./ExtensionsActionPayloads";
import ExtensionsActionTypes from "./ExtensionsActionTypes";
import { sortExtensions } from "./ExtensionsHelpers/SortExtensions";

interface State {
    loading: boolean;
    dialogOpen?: boolean;
    extensionId?: string | null;
    extensions: WebappExtension[];
    disabledExtensions: string[];
    loadingExtensionIds: string[];
    addRequestPendingExtensionIds: string[];
    successfullyInstalledExtensionIds: string[]; // for use with addExtensionSuccess
}

class ExtensionsStore extends ReduceStore<State, AllPayloads> {
    getInitialState(): State {
        return {
            loading: true,
            dialogOpen: false,
            extensionId: null,
            extensions: [],
            disabledExtensions: [],
            loadingExtensionIds: [],
            addRequestPendingExtensionIds: [],
            successfullyInstalledExtensionIds: []
        };
    }

    reset(state: State): State {
        state.extensions.forEach(extension => {
            extension.terminate();
        });

        return this.getInitialState();
    }

    loadExtensions(state: State, {
        bid,
        currentBid,
        extensions,
        disabledExtensions
    }: Payloads.LoadExtensions): State {
        if (currentBid !== bid) {
            return state;
        }

        state.extensions.forEach(extension => {
            extension.terminate();
        });

        const sortedExtensions = sortExtensions(extensions);

        return {
            ...state,
            loading: false,
            disabledExtensions,
            extensions: sortedExtensions
        };
    }

    load(state: State, { eid }: Payloads.Load): State {
        const { loadingExtensionIds } = state;

        return {
            ...state,
            loadingExtensionIds: loadingExtensionIds.concat(eid)
        };
    }

    loaded(state: State, { eid }: Payloads.Loaded): State {
        const { loadingExtensionIds, successfullyInstalledExtensionIds } = state;

        return {
            ...state,
            loadingExtensionIds: loadingExtensionIds.filter(id => id !== eid),
            successfullyInstalledExtensionIds: successfullyInstalledExtensionIds.concat(eid)
        };
    }

    failedToLoad(state: State, { eid }: Payloads.FailedToLoad): State {
        const { loadingExtensionIds } = state;

        return {
            ...state,
            loadingExtensionIds: loadingExtensionIds.filter(id => id !== eid)
        };
    }

    reload(state: State): State {
        const localExtensions = state.extensions.filter(e => e.local);

        if (!localExtensions.length) {
            return state;
        }

        localExtensions.forEach(extension => {
            // eslint-disable-next-line no-console
            console.log(`Local extension "${extension.name}" reloaded`);
            extension.reload();
        });

        return { ...state };
    }

    openSettings(state: State, {
        extensionId
    }: Pick<Payloads.OpenSettings, "extensionId">): State {
        return {
            ...state,
            dialogOpen: true,
            extensionId
        };
    }

    closeSettings(state: State): State {
        return {
            ...state,
            dialogOpen: false,
            extensionId: null
        };
    }

    add(state: State, {
        bid,
        extension,
        local,
        openSettings,
        currentBid
    }: Payloads.Add): State {
        const { extensions } = state;
        const found = extensions.find(e => extension.id === e.id);

        let newState = state;
        if (openSettings) {
            newState = this.openSettings(newState, { extensionId: extension.id });
        }

        if (found) {
            if (local) {
                found.reload();
            }

            return this.enable(newState, { eid: found.id });
        }

        if (bid !== currentBid) {
            return newState;
        }

        const newExtensions = sortExtensions(extensions.concat(extension));

        return {
            ...newState,
            extensions: newExtensions
        };
    }

    enable(state: State, {
        eid
    }: Pick<Payloads.Enable, "eid">): State {
        return {
            ...state,
            disabledExtensions: state.disabledExtensions.filter(extId => extId !== eid)
        };
    }

    disable(state: State, {
        eid
    }: Pick<Payloads.Disable, "eid">): State {
        return {
            ...state,
            disabledExtensions: state.disabledExtensions.concat(eid)
        };
    }

    updateOption(state: State, {
        eid,
        name,
        value
    }: Payloads.UpdateOption): State {
        const extensions = state.extensions.map(ext => {
            if (ext.id !== eid) {
                return ext;
            }

            const extension = new WebappExtension({ ...ext });
            extension.setOption(name, value);
            return extension;
        });

        return {
            ...state,
            extensions
        };
    }

    remove(state: State, {
        eid
    }: Payloads.Remove): State {
        const { extensions, successfullyInstalledExtensionIds } = state;

        return {
            ...state,
            extensions: extensions.filter(ext => ext.id !== eid),
            successfullyInstalledExtensionIds: successfullyInstalledExtensionIds.filter(id => id !== eid)
        };
    }

    addExtensionRequest(state: State, {
        eid
    }: Payloads.AddExtensionRequest) : State {
        const { addRequestPendingExtensionIds } = state;

        return {
            ...state,
            addRequestPendingExtensionIds: [...addRequestPendingExtensionIds, eid]
        };
    }

    addExtensionSuccess(state: State, {
        eid
    }: Payloads.AddExtensionSuccess) : State {
        const { addRequestPendingExtensionIds } = state;

        return {
            ...state,
            addRequestPendingExtensionIds: addRequestPendingExtensionIds.filter(id => id !== eid)
        };
    }

    addExtensionFailed(state: State, {
        eid
    }: Payloads.AddExtensionFailed) : State {
        const { addRequestPendingExtensionIds } = state;

        return {
            ...state,
            addRequestPendingExtensionIds: addRequestPendingExtensionIds.filter(id => id !== eid)
        };
    }

    // eslint-disable-next-line complexity
    reduce(state: State, action: AllPayloads): State {
        switch (action.type) {
            case BarrelActionTypes.RESET:
                return this.reset(state);

            case ExtensionsActionTypes.LOAD_EXTENSIONS:
                return this.loadExtensions(state, action);
            case ExtensionsActionTypes.RELOAD:
                return this.reload(state);
            case ExtensionsActionTypes.OPEN_SETTINGS:
                return this.openSettings(state, action);
            case ExtensionsActionTypes.CLOSE_SETTINGS:
                return this.closeSettings(state);
            case ExtensionsActionTypes.ADD:
                return this.add(state, action);
            case ExtensionsActionTypes.ENABLE:
                return this.enable(state, action);
            case ExtensionsActionTypes.UPDATE_OPTION:
                return this.updateOption(state, action);
            case ExtensionsActionTypes.DISABLE:
                return this.disable(state, action);
            case ExtensionsActionTypes.REMOVE:
                return this.remove(state, action);
            case ExtensionsActionTypes.ADD_EXTENSION_REQUEST:
                return this.addExtensionRequest(state, action);
            case ExtensionsActionTypes.ADD_EXTENSION_SUCCESS:
                return this.addExtensionSuccess(state, action);
            case ExtensionsActionTypes.ADD_EXTENSION_FAILED:
                return this.addExtensionFailed(state, action);
            case ExtensionsActionTypes.LOAD:
                return this.load(state, action);
            case ExtensionsActionTypes.LOADED:
                return this.loaded(state, action);
            case ExtensionsActionTypes.FAILED_TO_LOAD:
                return this.failedToLoad(state, action);
            default:
                return state;
        }
    }
}

export default ExtensionsStore;
export {
    State as ExtensionsStoreState
};
